import { useState } from 'react';
import { X, Lock, Eye, EyeOff, AlertTriangle, CheckCircle } from 'lucide-react';
import { colors } from '../styles/colors';
import { updatePassword } from '../services/airtable';

function ChangePasswordModal({ onClose }) {
  const [formData, setFormData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setError(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    // Validation
    if (formData.newPassword.length < 8) {
      setError('Le nouveau mot de passe doit contenir au moins 8 caractères');
      return;
    }

    if (formData.newPassword !== formData.confirmPassword) {
      setError('Les mots de passe ne correspondent pas');
      return;
    }

    if (formData.currentPassword === formData.newPassword) {
      setError('Le nouveau mot de passe doit être différent de l\'ancien');
      return;
    }

    setLoading(true);

    try {
      await updatePassword(formData.currentPassword, formData.newPassword);
      setSuccess(true);
      setTimeout(() => {
        onClose();
      }, 2000);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const inputStyle = {
    width: '100%',
    padding: '0.75rem',
    paddingRight: '2.5rem',
    border: `2px solid ${colors.secondary.lightTeal}`,
    borderRadius: '0.5rem',
    fontSize: '0.9375rem',
    fontFamily: '"Noto Sans", sans-serif',
    color: colors.primary.gray,
    backgroundColor: colors.text.white,
    outline: 'none',
    boxSizing: 'border-box'
  };

  const labelStyle = {
    display: 'block',
    fontSize: '0.875rem',
    fontWeight: '500',
    color: colors.primary.gray,
    marginBottom: '0.5rem',
    fontFamily: '"Noto Sans", sans-serif'
  };

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000
      }}
    >
      <div
        style={{
          backgroundColor: colors.text.white,
          borderRadius: '1rem',
          width: '90%',
          maxWidth: '450px',
          boxShadow: '0 10px 40px rgba(0, 0, 0, 0.2)'
        }}
      >
        {/* Header */}
        <div
          style={{
            padding: '1.25rem 1.5rem',
            backgroundColor: colors.primary.teal,
            borderRadius: '1rem 1rem 0 0',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <Lock size={24} color={colors.text.white} />
            <h2
              style={{
                margin: 0,
                fontFamily: '"Century Gothic Bold", "Noto Sans", sans-serif',
                fontSize: '1.25rem',
                color: colors.text.white,
                fontWeight: 'bold'
              }}
            >
              Changer le mot de passe
            </h2>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: '0.25rem'
            }}
          >
            <X size={24} color={colors.text.white} />
          </button>
        </div>

        {/* Body */}
        <div style={{ padding: '1.5rem' }}>
          {success ? (
            <div
              style={{
                padding: '2rem',
                textAlign: 'center',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '1rem'
              }}
            >
              <div
                style={{
                  width: '60px',
                  height: '60px',
                  borderRadius: '50%',
                  backgroundColor: '#d1fae5',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <CheckCircle size={32} color="#065f46" />
              </div>
              <p
                style={{
                  margin: 0,
                  fontSize: '1rem',
                  fontWeight: '500',
                  color: colors.primary.gray,
                  fontFamily: '"Noto Sans", sans-serif'
                }}
              >
                Mot de passe modifié avec succès !
              </p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
              {error && (
                <div
                  style={{
                    padding: '1rem',
                    borderRadius: '0.5rem',
                    backgroundColor: '#fee2e2',
                    border: '1px solid #ef4444',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.75rem'
                  }}
                >
                  <AlertTriangle size={20} color="#dc2626" />
                  <span style={{ color: '#991b1b', fontSize: '0.875rem' }}>{error}</span>
                </div>
              )}

              {/* Mot de passe actuel */}
              <div>
                <label style={labelStyle}>
                  Mot de passe actuel <span style={{ color: colors.primary.teal }}>*</span>
                </label>
                <div style={{ position: 'relative' }}>
                  <input
                    type={showCurrentPassword ? 'text' : 'password'}
                    name="currentPassword"
                    value={formData.currentPassword}
                    onChange={handleChange}
                    required
                    style={inputStyle}
                    onFocus={(e) => e.target.style.borderColor = colors.primary.teal}
                    onBlur={(e) => e.target.style.borderColor = colors.secondary.lightTeal}
                  />
                  <button
                    type="button"
                    onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                    style={{
                      position: 'absolute',
                      right: '0.75rem',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      padding: '0.25rem',
                      display: 'flex',
                      alignItems: 'center'
                    }}
                  >
                    {showCurrentPassword ? (
                      <EyeOff size={18} color={colors.secondary.mediumGray} />
                    ) : (
                      <Eye size={18} color={colors.secondary.mediumGray} />
                    )}
                  </button>
                </div>
              </div>

              {/* Nouveau mot de passe */}
              <div>
                <label style={labelStyle}>
                  Nouveau mot de passe <span style={{ color: colors.primary.teal }}>*</span>
                </label>
                <div style={{ position: 'relative' }}>
                  <input
                    type={showNewPassword ? 'text' : 'password'}
                    name="newPassword"
                    value={formData.newPassword}
                    onChange={handleChange}
                    required
                    style={inputStyle}
                    onFocus={(e) => e.target.style.borderColor = colors.primary.teal}
                    onBlur={(e) => e.target.style.borderColor = colors.secondary.lightTeal}
                  />
                  <button
                    type="button"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    style={{
                      position: 'absolute',
                      right: '0.75rem',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      padding: '0.25rem',
                      display: 'flex',
                      alignItems: 'center'
                    }}
                  >
                    {showNewPassword ? (
                      <EyeOff size={18} color={colors.secondary.mediumGray} />
                    ) : (
                      <Eye size={18} color={colors.secondary.mediumGray} />
                    )}
                  </button>
                </div>
                <p
                  style={{
                    margin: '0.375rem 0 0 0',
                    fontSize: '0.75rem',
                    color: colors.secondary.mediumGray,
                    fontFamily: '"Noto Sans", sans-serif'
                  }}
                >
                  Minimum 8 caractères
                </p>
              </div>

              {/* Confirmer le mot de passe */}
              <div>
                <label style={labelStyle}>
                  Confirmer le nouveau mot de passe <span style={{ color: colors.primary.teal }}>*</span>
                </label>
                <div style={{ position: 'relative' }}>
                  <input
                    type={showConfirmPassword ? 'text' : 'password'}
                    name="confirmPassword"
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    required
                    style={inputStyle}
                    onFocus={(e) => e.target.style.borderColor = colors.primary.teal}
                    onBlur={(e) => e.target.style.borderColor = colors.secondary.lightTeal}
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    style={{
                      position: 'absolute',
                      right: '0.75rem',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      padding: '0.25rem',
                      display: 'flex',
                      alignItems: 'center'
                    }}
                  >
                    {showConfirmPassword ? (
                      <EyeOff size={18} color={colors.secondary.mediumGray} />
                    ) : (
                      <Eye size={18} color={colors.secondary.mediumGray} />
                    )}
                  </button>
                </div>
              </div>

              {/* Buttons */}
              <div
                style={{
                  display: 'flex',
                  gap: '1rem',
                  justifyContent: 'flex-end',
                  marginTop: '0.5rem'
                }}
              >
                <button
                  type="button"
                  onClick={onClose}
                  disabled={loading}
                  style={{
                    padding: '0.75rem 1.5rem',
                    borderRadius: '0.5rem',
                    border: `2px solid ${colors.secondary.mediumGray}`,
                    backgroundColor: 'transparent',
                    color: colors.primary.gray,
                    fontSize: '1rem',
                    fontWeight: '600',
                    fontFamily: '"Noto Sans", sans-serif',
                    cursor: loading ? 'not-allowed' : 'pointer',
                    opacity: loading ? 0.5 : 1
                  }}
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  style={{
                    padding: '0.75rem 1.5rem',
                    borderRadius: '0.5rem',
                    border: 'none',
                    backgroundColor: loading ? colors.secondary.mediumGray : colors.primary.teal,
                    color: colors.text.white,
                    fontSize: '1rem',
                    fontWeight: '600',
                    fontFamily: '"Century Gothic Bold", "Noto Sans", sans-serif',
                    cursor: loading ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem'
                  }}
                >
                  {loading ? 'Modification...' : 'Modifier le mot de passe'}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}

export default ChangePasswordModal;
